import type { Author, ContractedBook } from '@/data/types';

export type BokmesseId = 'lillehammer' | 'frankfurt' | 'litteraturhuset';

export type BokmesseTriggerType = 'arlig' | 'ad-hoc';

export type BokmesseDefinition = {
  id: BokmesseId;
  navn: string;
  kortNavn: string;
  beskrivelse: string;
  triggerDagIAaret: number | null;
  triggerType: BokmesseTriggerType;
  topBorderHex: number;
  aksentFarge: number;
};

export const BOKMESSER: ReadonlyArray<BokmesseDefinition> = [
  {
    id: 'lillehammer',
    navn: 'Lillehammer Litteraturfestival',
    kortNavn: 'Lillehammer',
    beskrivelse:
      'Sjikt og snø. Forfatterne mingler, juryen veier, agentene drikker hvitvin. Hva gjør du?',
    triggerDagIAaret: 30,
    triggerType: 'arlig',
    topBorderHex: 0xd9b382,
    aksentFarge: 0xd9b382,
  },
  {
    id: 'frankfurt',
    navn: 'Frankfurter Buchmesse',
    kortNavn: 'Frankfurt',
    beskrivelse:
      'Storebror-messa. Internasjonale agenter, oversettelses-deals, og du kommer til å se Kapelle Damm sitt monter fra langt unna.',
    triggerDagIAaret: 70,
    triggerType: 'arlig',
    topBorderHex: 0x2c3e6b,
    aksentFarge: 0x2c3e6b,
  },
  {
    id: 'litteraturhuset',
    navn: 'Litteraturhuset Oslo',
    kortNavn: 'Litteraturhuset',
    beskrivelse:
      'Litteraturhuset i Oslo vil ha lanseringskveld med Q&A, vin og signering.',
    triggerDagIAaret: null,
    triggerType: 'ad-hoc',
    topBorderHex: 0x862736,
    aksentFarge: 0x862736,
  },
];

export function finnBokmesse(id: BokmesseId): BokmesseDefinition | undefined {
  return BOKMESSER.find((m) => m.id === id);
}

export const LILLEHAMMER_DAG = 30;
export const FRANKFURT_DAG = 70;

export const FRANKFURT_MIN_UTGITTE = 3;
export const FRANKFURT_MIN_OMDOMME = 40;

export const LITTERATURHUSET_SALG_GRENSE = 5_000;
export const LITTERATURHUSET_SJANSE = 0.3;

export const FRANKFURT_OVERSETTELSE_SJANSE_FULL = 0.4;
export const FRANKFURT_OVERSETTELSE_SJANSE_LITEN = 0.15;
export const FRANKFURT_OVERSETTELSE_SJANSE_NETTVERK = 0.05;

export const FRANKFURT_TILBUD_MIN_DAGER = 5;
export const FRANKFURT_TILBUD_MAX_DAGER = 14;

export const OVERSETTELSE_FORSKUDD = 200_000;

export type InternasjonalAvtale = {
  isbn: string;
  tittel: string;
  forfatterNavn: string;
  sprak: string;
  dag: number;
  forlagId?: string;
  forlagNavn?: string;
  flagg?: string;
  engangsBelop?: number;
  royaltyPerSalg?: number;
  totalRoyaltyMottatt?: number;
};

export type PlanlagtFrankfurtTilbud = {
  dag: number;
  bokIsbn: string;
};

export const FRANKFURT_SPRAK: ReadonlyArray<{ sprak: string; forlag: string }> = [
  { sprak: 'engelsk', forlag: 'Penguin Random House' },
  { sprak: 'tysk', forlag: 'Suhrkamp Verlag' },
  { sprak: 'fransk', forlag: 'Gallimard' },
  { sprak: 'nederlandsk', forlag: 'De Geus' },
  { sprak: 'spansk', forlag: 'Anagrama' },
  { sprak: 'italiensk', forlag: 'Iperborea' },
];

export function flaggLillehammer(aar: number): string {
  return `bokmesse_lillehammer_${aar}`;
}

export function flaggFrankfurt(aar: number): string {
  return `bokmesse_frankfurt_${aar}`;
}

export function flaggLitteraturhuset(isbn: string): string {
  return `bokmesse_lithus_${isbn}`;
}

export function eventIdLillehammer(aar: number): string {
  return `synth_bokmesse_lillehammer_${aar}`;
}

export function eventIdFrankfurt(aar: number): string {
  return `synth_bokmesse_frankfurt_${aar}`;
}

export function eventIdLitteraturhuset(isbn: string, dag: number): string {
  return `synth_bokmesse_lithus_${isbn}_${dag}`;
}

export function eventIdFrankfurtTilbud(isbn: string, dag: number): string {
  return `synth_bokmesse_oversettelse_${isbn}_${dag}`;
}

export function snittStats(forfatter: Author): number {
  const s = forfatter.stats;
  return (
    s.dialog +
    s.plot +
    s.karakter +
    s.spraak +
    s.voice +
    s.autentisitet +
    s.disiplin
  ) / 7;
}

export function velgBesteForfatter(
  authors: Record<string, Author>,
): Author | null {
  let beste: Author | null = null;
  let bestSnitt = -1;
  for (const a of Object.values(authors)) {
    if (a.paaKurs) continue;
    const snitt = snittStats(a);
    if (snitt > bestSnitt) {
      bestSnitt = snitt;
      beste = a;
    }
  }
  return beste;
}

export function velgFrankfurtTilbudsBok(
  utgitteBoker: ReadonlyArray<ContractedBook>,
  iProduksjonUtgitte: ReadonlyArray<ContractedBook>,
  internasjonale: ReadonlyArray<InternasjonalAvtale>,
  rng: () => number,
): ContractedBook | null {
  const alle: ContractedBook[] = [...utgitteBoker, ...iProduksjonUtgitte];
  const ledige = alle.filter(
    (b) => !internasjonale.some((a) => a.isbn === b.isbn),
  );
  if (ledige.length === 0) return null;
  ledige.sort((a, b) => {
    const snittA = anmeldelseSnitt(a);
    const snittB = anmeldelseSnitt(b);
    if (snittB !== snittA) return snittB - snittA;
    return b.salg - a.salg;
  });
  if (rng() < 0.7) return ledige[0];
  const topp = ledige.slice(0, Math.min(3, ledige.length));
  const idx = Math.floor(rng() * topp.length);
  return topp[Math.min(topp.length - 1, idx)];
}

function anmeldelseSnitt(bok: ContractedBook): number {
  if (bok.anmeldelser.length === 0) return 0;
  let s = 0;
  for (const a of bok.anmeldelser) s += a.terninger;
  return s / bok.anmeldelser.length;
}

export function velgFrankfurtSprak(rng: () => number): { sprak: string; forlag: string } {
  const idx = Math.floor(rng() * FRANKFURT_SPRAK.length);
  return FRANKFURT_SPRAK[Math.min(FRANKFURT_SPRAK.length - 1, idx)];
}
